import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { TIPO_DE_BEM, TIPOS_DE_BEM, competenciaAtual, type TipoDeBem } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { reais, textoParaCentavos } from "@/lib/dinheiro";
import { campo, rotulo } from "../estilos";

export type BemInicial = { id?: string; nome: string; tipo: TipoDeBem; valorCents: number; compradoEm: string; vidaUtilMeses: number; valorResidualCents: number; lancamentoId?: string | null };
export const bemVazio = (): BemInicial => ({ nome: "", tipo: "MAQUINA", valorCents: 0, compradoEm: competenciaAtual(), vidaUtilMeses: TIPO_DE_BEM.MAQUINA.vidaUtilMeses, valorResidualCents: 0 });

/** Cadastro de um bem para a depreciação: valor, mês da compra e vida útil. */
export function BemDialog({ inicial, onFechar }: { inicial: BemInicial | null; onFechar: () => void }) {
  const utils = trpc.useUtils();
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState<TipoDeBem>("MAQUINA");
  const [valor, setValor] = useState("");
  const [mes, setMes] = useState(competenciaAtual());
  const [anos, setAnos] = useState("10");
  const [residual, setResidual] = useState("");
  useEffect(() => {
    if (!inicial) return;
    setNome(inicial.nome);
    setTipo(inicial.tipo);
    setValor(inicial.valorCents ? reais(inicial.valorCents) : "");
    setMes(inicial.compradoEm);
    setAnos(String(inicial.vidaUtilMeses / 12).replace(".", ","));
    setResidual(inicial.valorResidualCents ? reais(inicial.valorResidualCents) : "");
  }, [inicial]);
  const salvar = trpc.financeiro.bens.salvar.useMutation({
    onSuccess: () => {
      toast.success("Bem salvo. A depreciação já entra no DRE.");
      utils.financeiro.invalidate();
      onFechar();
    },
    onError: (e) => toast.error(e.message),
  });
  const valorCents = textoParaCentavos(valor);
  const residualCents = textoParaCentavos(residual) || 0;
  const meses = Math.round(Number(anos.replace(",", ".")) * 12);
  const valido = nome.trim().length >= 2 && valorCents > 0 && !!mes && meses >= 1 && meses <= 600 && residualCents < valorCents;
  const mudarTipo = (t: TipoDeBem) => {
    setTipo(t);
    setAnos(String(TIPO_DE_BEM[t].vidaUtilMeses / 12));
  };

  return (
    <Dialog open={!!inicial} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="max-w-lg border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle>{inicial?.id ? "Editar bem" : "Novo bem"}</DialogTitle>
          <DialogDescription>A depreciação divide o valor pela vida útil e entra todo mês no DRE, depois do EBITDA. Não mexe no caixa.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-12">
          <label className="sm:col-span-12">
            <span className={rotulo}>Nome</span>
            <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Fresadora 5 eixos" className={campo} />
          </label>
          <label className="sm:col-span-7">
            <span className={rotulo}>Tipo</span>
            <select value={tipo} onChange={(e) => mudarTipo(e.target.value as TipoDeBem)} className={campo}>
              {TIPOS_DE_BEM.map((t) => (
                <option key={t} value={t}>
                  {TIPO_DE_BEM[t].rotulo}
                </option>
              ))}
            </select>
          </label>
          <label className="sm:col-span-5">
            <span className={rotulo}>Vida útil (anos)</span>
            <input value={anos} onChange={(e) => setAnos(e.target.value)} inputMode="decimal" className={campo} />
          </label>
          <label className="sm:col-span-6">
            <span className={rotulo}>Valor da compra</span>
            <input value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" placeholder="0,00" className={campo} />
          </label>
          <label className="sm:col-span-6">
            <span className={rotulo}>Mês da compra</span>
            <input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className={campo} />
          </label>
          <label className="sm:col-span-12">
            <span className={rotulo}>Valor residual (opcional)</span>
            <input value={residual} onChange={(e) => setResidual(e.target.value)} inputMode="decimal" placeholder="0,00" className={campo} />
            <span className="mt-1 block text-[11px] text-zinc-500 dark:text-muted-foreground">
              Quanto o bem ainda vale no fim da vida útil (revenda). A vida útil sugerida é a da tabela da Receita para o tipo.
              {valido && ` Depreciação: ${reais(Math.floor((valorCents - residualCents) / meses))} por mês.`}
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onFechar} className="h-9 rounded-md px-4 text-sm text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted">
            Cancelar
          </button>
          <button
            type="button"
            disabled={!valido || salvar.isPending}
            onClick={() => salvar.mutate({ id: inicial?.id, nome: nome.trim(), tipo, valorCents, compradoEm: mes, vidaUtilMeses: meses, valorResidualCents: residualCents, lancamentoId: inicial?.lancamentoId ?? null })}
            className="h-9 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            Salvar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
